import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase-config';

// Emails that auto-bootstrap an authorized_users document on first sign-in.
const OWNER_EMAILS = [
  "admin@ramdesignworks.com",
  "rmcmanus@ramdesignworks.com",
];

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUser(null);
        setUserProfile(null);
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      try {
        const docRef = doc(db, 'authorized_users', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setUserProfile(docSnap.data());
          setIsAuthorized(true);
        } else if (OWNER_EMAILS.includes(user.email?.toLowerCase())) {
          // Owner bootstrap — auto-approve on first sign-in
          const ownerProfile = {
            uid: user.uid,
            email: user.email,
            role: 'admin',
            approvedAt: serverTimestamp(),
          };
          await setDoc(docRef, ownerProfile);
          setUserProfile(ownerProfile);
          setIsAuthorized(true);
        } else {
          // Authenticated but not in authorized_users — bounced
          setIsAuthorized(false);
        }
      } catch (err) {
        console.error('Auth guard error:', err);
        setError(err.message);
        setIsAuthorized(false);
      }

      setLoading(false);
    }, (err) => {
      setError(err.message);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    isAuthorized,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
